// TTS抽象化レイヤ
// 3エンジン対応: Web Speech API / OpenAI TTS / ElevenLabs
import { AppSettings, SpeechEngine } from '../types';

export interface SpeakOptions {
  /** 強調モード（Lv2の予告レベル等で使う） */
  emphasis?: 'soft' | 'normal' | 'strong';
}

// Safari/Chrome の既知バグ対策: 発話中のutteranceがGCされると canceled エラーになるため、
// モジュールスコープで参照を保持してGCを防ぐ
let activeUtterances: SpeechSynthesisUtterance[] = [];

/**
 * 音声でテキストを読み上げる
 * @returns 再生完了まで待機するPromise
 */
export async function speak(
  text: string,
  settings: AppSettings,
  opts: SpeakOptions = {}
): Promise<void> {
  if (!text) return;
  switch (settings.speechEngine) {
    case 'openai-tts':
      return speakViaOpenAI(text, settings, opts);
    case 'elevenlabs':
      return speakViaElevenLabs(text, settings, opts);
    case 'web-speech':
    default:
      return speakViaWebSpeech(text, settings, opts);
  }
}

// ===== Web Speech API =====
function speakViaWebSpeech(text: string, s: AppSettings, opts: SpeakOptions): Promise<void> {
  return new Promise((resolve, reject) => {
    const synth = window.speechSynthesis;
    if (!synth) {
      reject(new Error('このブラウザは Web Speech API に対応していません'));
      return;
    }

    const u = new SpeechSynthesisUtterance(text);
    // GC防止のためモジュールスコープに保持（Safari/Chrome の canceled バグ対策）
    activeUtterances.push(u);
    const cleanup = () => {
      activeUtterances = activeUtterances.filter((x) => x !== u);
    };

    u.lang = 'ja-JP';
    u.volume = Math.max(0, Math.min(1, s.volume));
    u.rate = Math.max(0.5, Math.min(2, s.rate));
    u.pitch = opts.emphasis === 'strong' ? 1.2 : opts.emphasis === 'soft' ? 0.9 : 1.0;

    // 日本語の声を優先して選択
    const voices = synth.getVoices();
    const jaVoice = voices.find((v) => v.lang.startsWith('ja'));
    if (jaVoice) u.voice = jaVoice;

    u.onend = () => {
      cleanup();
      resolve();
    };
    u.onerror = (e) => {
      cleanup();
      // canceled / interrupted は連続発話やユーザー操作で起こる無害なケース → エラー扱いしない
      if (e.error === 'canceled' || e.error === 'interrupted') {
        resolve();
        return;
      }
      reject(new Error(`SpeechSynthesisError: ${e.error}`));
    };

    // cancel() は直前の発話が残っているときだけ（cancel直後のspeakがcanceledを誘発するため最小限に）
    if (synth.speaking || synth.pending) {
      synth.cancel();
    }
    synth.speak(u);
  });
}

// ===== OpenAI TTS（ai-proxy経由 → Pages Function） =====
async function speakViaOpenAI(text: string, s: AppSettings, opts: SpeakOptions): Promise<void> {
  const apiBase = s.apiBase || '';
  const url = `${apiBase}/api/tts`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, engine: 'openai', voice: 'nova' }),
    });
  } catch (err) {
    console.warn('[speech] OpenAI TTS接続失敗、Web Speechにフォールバック', err);
    return speakViaWebSpeech(text, s, opts);
  }
  if (!res.ok) {
    console.warn(`[speech] OpenAI TTS未配備 (${res.status})、Web Speechにフォールバック`);
    return speakViaWebSpeech(text, s, opts);
  }
  const blob = await res.blob();
  await playBlob(blob, s);
}

// ===== ElevenLabs =====
async function speakViaElevenLabs(text: string, s: AppSettings, opts: SpeakOptions): Promise<void> {
  if (!s.elevenLabsApiKey) {
    console.warn('[speech] ElevenLabs APIキー未設定、Web Speechにフォールバック');
    return speakViaWebSpeech(text, s, opts);
  }
  const voiceId = s.elevenLabsVoiceId || '21m00Tcm4TlvDq8ikWAM'; // Rachel（デフォルト）
  const url = `https://api.elevenlabs.io/v1/text-to-speech/${voiceId}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'xi-api-key': s.elevenLabsApiKey,
    },
    body: JSON.stringify({
      text,
      model_id: 'eleven_multilingual_v2',
      voice_settings: { stability: 0.5, similarity_boost: 0.75 },
    }),
  });
  if (!res.ok) {
    console.warn(`[speech] ElevenLabs失敗 (${res.status})、Web Speechにフォールバック`);
    return speakViaWebSpeech(text, s, opts);
  }
  const blob = await res.blob();
  await playBlob(blob, s);
}

// ===== 共通: Blob再生 =====
function playBlob(blob: Blob, s: AppSettings): Promise<void> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    audio.volume = Math.max(0, Math.min(1, s.volume));
    audio.playbackRate = Math.max(0.5, Math.min(2, s.rate));
    audio.onended = () => {
      URL.revokeObjectURL(url);
      resolve();
    };
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('音声再生失敗'));
    };
    audio.play().catch(reject);
  });
}

/** ブラウザがTTSをサポートするか */
export function isSpeechAvailable(engine: SpeechEngine): boolean {
  if (engine === 'web-speech') return 'speechSynthesis' in window;
  return true; // OpenAI/ElevenLabsはネット経由なので常にtrue（API失敗は実行時に検知）
}

/** Web Speechの声を事前ロード（initialization用） */
export function preloadVoices(): void {
  if ('speechSynthesis' in window) {
    window.speechSynthesis.getVoices();
  }
}
